import React, { CSSProperties, useEffect, useRef, useState } from "react";
import Spinner from "../Spinner/Spinner";

interface IProps {
  title?: string;
  src?: string;
  width?: string;
  style?: CSSProperties;
  blob?: Blob;
}

const DISABLED_TYPES = [
  "text/csv",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "text/plain",
];
const AUTORESIZE_DISABLED_TYPES = ["application/pdf"];

const IFrame: React.FC<IProps> = ({
  title = "Lataa liite",
  src,
  width = "100%",
  style = {},
  blob,
}) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [height, setHeight] = useState("600px");
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    setIsLoading(true);
    let url: string | null = null;
    if (blob) {
      url = URL.createObjectURL(blob);
      setObjectUrl(url);
    } else if (src) {
      setObjectUrl(src);
    }
    setIsLoading(false);

    return () => {
      if (url) {
        URL.revokeObjectURL(url);
      }
    };
  }, [blob, src]);

  if (isLoading) {
    return <Spinner />;
  }

  const activeUrl = objectUrl || src;
  const lowerTitle = (title || "").toLowerCase();

  const isImage = (blob?.type && blob.type.startsWith("image")) ||
    lowerTitle.endsWith(".jpg") || lowerTitle.endsWith(".jpeg") || lowerTitle.endsWith(".png") ||
    lowerTitle.endsWith(".gif") || lowerTitle.endsWith(".webp") || lowerTitle.endsWith(".svg");

  const isPdf = blob?.type === "application/pdf" || lowerTitle.endsWith(".pdf");

  const isDisabled = DISABLED_TYPES.includes(blob?.type ?? "");
  if (isDisabled && !isImage && !isPdf) {
    return (
      <a href={activeUrl || undefined} target="_blank" rel="noreferrer" style={{ fontWeight: 'bold' }}>
        Lataa / Avaa tiedosto: {title}
      </a>
    );
  }

  const onLoad = () => {
    if (!iframeRef.current?.contentWindow) return;
    try {
      setHeight(iframeRef.current.contentWindow.document.body.scrollHeight + "px");
    } catch (e) {
      setHeight("600px");
    }
  };

  const autoResize = !AUTORESIZE_DISABLED_TYPES.includes(blob?.type ?? "") && !isPdf;

  if (isImage) {
    return (
      <div style={{ width, display: "flex", flexDirection: "column", gap: "0.5rem" }}>
        <div style={{ marginBottom: "0.25rem" }}>
          <a
            href={activeUrl || undefined}
            target="_blank"
            rel="noreferrer"
            download={title}
            style={{ fontWeight: "bold", textDecoration: "underline", fontSize: "0.9rem" }}
          >
            Avaa kuva täysikokoisena / Lataa ({title})
          </a>
        </div>
        <img
          alt={title}
          src={activeUrl || undefined}
          style={{ maxWidth: "100%", maxHeight: "800px", objectFit: "contain", borderRadius: "4px", border: "1px solid #ddd", ...style }}
        />
      </div>
    );
  }

  return (
    <div style={{ width, display: "flex", flexDirection: "column", gap: "0.5rem" }}>
      {blob?.type === "application/pdf" && (
        <div style={{ marginBottom: "0.5rem" }}>
          <a
            href={activeUrl || undefined}
            target="_blank"
            rel="noreferrer"
            download={title}
            style={{ fontWeight: "bold", textDecoration: "underline", fontSize: "0.9rem" }}
          >
            Avaa / Lataa PDF uuteen välilehteen
          </a>
        </div>
      )}
      <iframe
        title={title}
        src={activeUrl || undefined}
        width={"100%"}
        height={autoResize ? height : "600px"}
        style={{ border: "1px solid #ccc", borderRadius: "4px", ...style }}
        ref={iframeRef}
        onLoad={autoResize ? onLoad : undefined}
      />
    </div>
  );
};

export default IFrame;
